import React, { useCallback, useEffect, useState } from 'react';
import { invoke } from '@tauri-apps/api/core';
import { Toggle, Row, Section } from '../settingsPrimitives';
import { isTauri } from '../../utils/tauri';
import McpActivityModal from './McpActivityModal';
import McpSetupSteps, { setupState } from './McpSetupSteps';
import McpTerminalGrants from './McpTerminalGrants';
import McpAttachmentFolders from './McpAttachmentFolders';
import './McpSettings.css';

/**
 * Settings → MCP server.
 *
 * Apiboo is the MCP **server**: an external client (Claude Code, Claude
 * Desktop through the helper program, Cursor, Zed…) connects to a loopback
 * endpoint and reads or acts on this app's boards and documents.
 *
 * Everything here is off by default and stays off until four deliberate acts,
 * shown as numbered steps (`McpSetupSteps`, owner 11.10.2026): generate a
 * token, tick a capability, switch the server on, paste the setup into a
 * client. A fresh install with the server running and a token minted still
 * answers `tools/list` with an empty array.
 *
 * Shape copied from `VaultExtensionSettings`'s bridge section — a settings
 * `Toggle` that starts and stops a local server, initial state from a status
 * command.
 */
export default function McpSettings({ t }) {
  const [status, setStatus] = useState(null);
  const [settings, setSettings] = useState(null);
  const [bridge, setBridge] = useState(null);
  const [bridgeBusy, setBridgeBusy] = useState(false);
  const [bridgeError, setBridgeError] = useState(null);
  const [token, setToken] = useState('');
  const [activity, setActivity] = useState([]);
  const [activityOpen, setActivityOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [portDraft, setPortDraft] = useState('');
  // Every invoke below used to fail into `console.warn` alone. That is what a
  // user sees as "I click the toggle and nothing happens" — reported by the
  // owner 09.09.2026, whose `tauri dev` predated this feature's Rust code, so
  // every `mcp_*` command answered "Command not found" while the page rendered
  // perfectly (the JS hot-reloads; the Rust side does not). A silent catch is
  // indistinguishable from a dead control, so failures are surfaced here.
  const [backendError, setBackendError] = useState(null);

  const refresh = useCallback(async () => {
    if (!isTauri()) return;
    try {
      const [s, cfg, br, act] = await Promise.all([
        invoke('mcp_status'),
        invoke('mcp_settings_get'),
        invoke('mcp_bridge_status'),
        invoke('mcp_activity'),
      ]);
      setStatus(s || null);
      setSettings(cfg || null);
      setBridge(br || null);
      setActivity(Array.isArray(act) ? act : []);
      if (cfg?.port != null) setPortDraft(String(cfg.port));
      setBackendError(null);
    } catch (err) {
      console.warn('[mcp] status read failed:', err);
      setBackendError(String(err?.message || err));
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Step 4 turns green by itself: while the server runs and no agent has
  // reached it yet (or the last one used an old key), re-read the status.
  const waitingForAgent = !!status?.running && !setupState({ settings, status })[3];
  useEffect(() => {
    if (!waitingForAgent || !isTauri()) return undefined;
    const id = setInterval(() => {
      invoke('mcp_status').then((s) => s && setStatus(s), () => {});
    }, 3000);
    return () => clearInterval(id);
  }, [waitingForAgent]);

  const patch = useCallback(
    async (next) => {
      try {
        const cfg = await invoke('mcp_settings_set', { patch: next });
        setSettings(cfg || null);
        setBackendError(null);
      } catch (err) {
        console.warn('[mcp] settings write failed:', err);
        setBackendError(String(err?.message || err));
      }
    },
    []
  );

  const handleServerToggle = useCallback(
    async (next) => {
      if (!isTauri() || busy) return;
      // No key, no server (it would refuse to start): step 1 says so.
      if (next && !settings?.hasToken) return;
      setBusy(true);
      await patch({ enabled: next });
      try {
        if (next) await invoke('mcp_start');
        else await invoke('mcp_stop');
      } catch (err) {
        // A fail-closed bind carries its reason in `status.lastError`. Anything
        // else — a missing command, a panic — has no other home, so it lands here.
        console.warn('[mcp] server toggle failed:', err);
        setBackendError(String(err?.message || err));
      }
      await refresh();
      setBusy(false);
    },
    [busy, patch, refresh, settings?.hasToken]
  );

  const handleGenerateToken = useCallback(async () => {
    try {
      const fresh = await invoke('mcp_generate_token');
      setToken(fresh);
      // The switch was turned on, but the start was refused for lack of a
      // token — the usual order after a sign-out wipes both. Finish the start
      // the user already asked for instead of leaving it stopped. A server
      // that is already running keeps serving; it re-reads the hash per request.
      if (settings?.enabled && !status?.running) {
        try {
          await invoke('mcp_start');
        } catch (err) {
          console.warn('[mcp] start after token generation failed:', err);
        }
      }
      await refresh();
      setBackendError(null);
    } catch (err) {
      console.warn('[mcp] token generation failed:', err);
      setBackendError(String(err?.message || err));
    }
  }, [refresh, settings?.enabled, status?.running]);

  const handleInstallBridge = useCallback(async () => {
    setBridgeBusy(true);
    setBridgeError(null);
    try {
      const next = await invoke('mcp_bridge_install');
      setBridge(next || null);
    } catch (err) {
      // Rust returns a fixed code; McpBridgeInstall maps it to a sentence.
      setBridgeError(typeof err === 'string' ? err : 'bridge_download_failed');
    }
    setBridgeBusy(false);
    await refresh();
  }, [refresh]);

  const port = settings?.port ?? 8762;
  const endpoint = status?.endpoint || `http://127.0.0.1:${port}/mcp`;
  const bridgePath =
    bridge && (bridge.state === 'installed' || bridge.state === 'update_available')
      ? bridge.path
      : null;

  return (
    <div className="space-y-6 mcp-settings">
      {backendError && (
        <div className="mcp-backend-error" role="alert">
          {/* Deliberately NOT the decorative warning strip O5 rules out — this
              is a real failure the user just triggered, not a standing caveat. */}
          {t('mcp.backendUnavailable')}
          <div className="mcp-backend-error-detail">{backendError}</div>
        </div>
      )}
      <Section title={t('mcp.setupTitle')}>
        <McpSetupSteps
          t={t}
          settings={settings}
          status={status}
          token={token}
          busy={busy}
          onGenerateToken={handleGenerateToken}
          onServerToggle={handleServerToggle}
          onCapabilities={(capabilities) => patch({ capabilities })}
          endpoint={endpoint}
          bridge={bridge}
          bridgeBusy={bridgeBusy}
          bridgeError={bridgeError}
          onInstallBridge={handleInstallBridge}
          bridgePath={bridgePath}
        />
      </Section>

      {/* The Terminal section sits between the capability matrix and the
          destructive switch on purpose: the matrix is where the surface is
          granted, this is where it is POINTED AT SOMETHING, and the switch
          below is the third thing that has to be on. Rendered only when the
          build actually has terminal tools, on the same `availableSurfaces`
          rule as every capability row. */}
      {(settings?.availableSurfaces || []).includes('terminal') && (
        <Section title={t('mcp.terminalTitle')}>
          <McpTerminalGrants t={t} />
        </Section>
      )}

      {(settings?.availableSurfaces || []).includes('attachments') && (
        <Section title={t('mcp.attachmentsTitle', 'Attachments')}>
          <McpAttachmentFolders t={t} folders={settings?.attachmentFolders || []} anyFolder={settings?.attachmentAnyFolder !== false} onChange={(cfg) => cfg && setSettings(cfg)} />
        </Section>
      )}

      <Section title={t('mcp.destructiveTitle')}>
        <Row label={t('mcp.destructiveEnable')} desc={t('mcp.destructiveEnableDesc')}>
          <Toggle
            value={!!settings?.destructive}
            onChange={(destructive) => patch({ destructive })}
          />
        </Row>
      </Section>

      <Section title={t('mcp.advancedTitle')}>
        <Row label={t('mcp.port')} desc={t('mcp.portDesc')}>
          <input
            type="number"
            className="settings-number-input"
            min={1024}
            max={65535}
            value={portDraft}
            onChange={(e) => setPortDraft(e.target.value)}
            onBlur={() => {
              const next = Number.parseInt(portDraft, 10);
              if (Number.isFinite(next) && next >= 1024 && next <= 65535) patch({ port: next });
              else setPortDraft(String(port));
            }}
          />
        </Row>
      </Section>

      <Section title={t('mcp.activityTitle')}>
        <div className="settings-row-desc mcp-note">{t('mcp.activityDesc')}</div>
        <Row label={t('mcp.activityCount', { count: activity.length })} desc={null}>
          <button type="button" className="settings-action-btn" onClick={() => setActivityOpen(true)}>
            {t('mcp.activityShow')}
          </button>
        </Row>
        <McpActivityModal
          t={t}
          isOpen={activityOpen}
          onClose={() => setActivityOpen(false)}
          activity={activity}
        />
      </Section>

      <Section title={t('mcp.neverTitle')}>
        <div className="settings-row-desc mcp-note">{t('mcp.neverList')}</div>
      </Section>
    </div>
  );
}
